/**
 * Prefetch handoff between src/app.tsx and src/views/Board.tsx (T8,
 * docs/performance.md C2). The startup waterfall used to be HTML, JS,
 * auth/me, then the board's request; a 401 on the board fails in exactly
 * the same case auth/me reports "not authenticated", so there is no reason
 * the board's request should wait for auth/me to resolve first. app.tsx now
 * starts it right away (once a peon name is stored and the landing route is
 * a board) and stashes the in-flight promise here; Board.tsx picks it up on
 * its own first load instead of firing a second, redundant request.
 *
 * A tiny module-level store rather than React state or context because
 * app.tsx and Board.tsx don't otherwise share anything at this point: the
 * fetch starts before auth is known, and Board only mounts several renders
 * later once auth resolves as authenticated.
 */

import type { BoardSummary, TaskStatus } from "../shared/types";

/** The same shape api.board.summary (src/api.ts) takes: the request the open or done board's first load sends. */
export interface BoardRequestOpts {
  status: TaskStatus;
  name: string;
  since: string | null;
  /** Start of the previous month (startOfLastMonth): the Done board's card header names that month's winner. */
  prev?: string | null;
  claims: boolean;
  flash: boolean;
  notes: boolean;
  limit?: number;
  before?: string;
}

/**
 * Builds the same query string api.board.summary sends, so a prefetch
 * started from one opts object is found by a lookup built from an equal
 * one. Keep this in sync with the URLSearchParams building in src/api.ts.
 */
export function boardRequestKey(opts: BoardRequestOpts): string {
  const q = new URLSearchParams({ status: opts.status, name: opts.name });
  if (opts.since) q.set("since", opts.since);
  if (opts.prev) q.set("prev", opts.prev);
  if (opts.claims) q.set("claims", "1");
  if (opts.flash) q.set("flash", "1");
  if (opts.notes) q.set("notes", "1");
  if (opts.limit) q.set("limit", String(opts.limit));
  if (opts.before) q.set("before", opts.before);
  return q.toString();
}

/**
 * Start of the current calendar month on this device, as ISO UTC. Lives
 * here (moved from Board.tsx) so app.tsx's prefetch and Board's own first
 * load compute the exact same `since`, and therefore the same request key.
 */
export function startOfThisMonth(): string {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString();
}

/**
 * Start of the previous calendar month on this device (December's for a January device), as ISO
 * UTC: the Done board's `prev`, so the Peon of the Month header names last month's winner
 * (docs/bounties.md "The crown is last month's"). Same logic as startOfThisMonth, one month back.
 */
export function startOfLastMonth(now: Date = new Date()): string {
  return new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString();
}

interface Entry {
  promise: Promise<BoardSummary>;
  at: number;
}

const store = new Map<string, Entry>();

/** A stashed prefetch older than this is treated as gone: Board.tsx falls back to its own request rather than await something that may have been sitting around from an earlier, unrelated visit. */
const MAX_AGE_MS = 10_000;

/** Stashes an in-flight board request under `key` so takePrefetchedBoard can pick it up once (Board.tsx's first load). */
export function prefetchBoard(key: string, promise: Promise<BoardSummary>, now: number = Date.now()): void {
  store.set(key, { promise, at: now });
}

/**
 * Returns and removes the promise stashed under `key`, or null if there
 * isn't one or it's older than MAX_AGE_MS. A prefetch is consumed at most
 * once: the second call for the same key always sees null, whether or not
 * the first call happened.
 */
export function takePrefetchedBoard(key: string, now: number = Date.now()): Promise<BoardSummary> | null {
  const entry = store.get(key);
  if (!entry) return null;
  store.delete(key);
  if (now - entry.at > MAX_AGE_MS) return null;
  return entry.promise;
}

/**
 * Drops every stashed prefetch. Called when the gate signs the device in: a board requested at
 * launch, before there was a session, can only have failed with 401, and the board that mounts
 * now must ask again rather than show that.
 */
export function forgetPrefetchedBoards(): void {
  store.clear();
}
